/**
 * The Advanced → Agents detail page, redesigned (task 1312; report
 * `project/reports/innovate-app-agents-page-redesign.md`, revision 3).
 *
 * The AI Service Config tab's agent panels, without a flow, over five tabs on
 * `?tab=` — the mechanism `ServiceDetailPage` uses, so a link can land on a tab
 * and Back works: **Overview · Prompt · Input & Output · Runs · History**.
 *
 * Editing happens in the panels. Edit (on Overview and Input & Output only;
 * everything on the other tabs saves as it happens) turns the header and that
 * tab's panels into their editing variants in place, locks the other tabs and
 * dims the Danger zone until Save changes or Cancel. Save keeps the rule task
 * 1109 set on the previous page: one PATCH carrying only the writable keys
 * whose value differs from the loaded agent. Immediate mutations (prompt
 * change, MCP add/remove, knowledge attach/detach) still save on action.
 *
 * History (the tab and the Prompt tab's History sub tab) follows the account's
 * adoption of Publishing & versions: a dormant account sees four tabs and no
 * version vocabulary, the rule `ResourceVersionControls` applies everywhere.
 *
 * Behind `innovate.fe.agents.redesign`; off, `App.tsx` routes to the previous
 * `pages/AgentDetailPage.tsx` unchanged.
 */
import { useCallback, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import { CheckIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Breadcrumbs from '@/components/layout/Breadcrumbs';
import DeleteResourceControl from '@/components/shared/DeleteResourceControl';
import { AgentIdentityHeader } from '@/components/services/detail/agents/AgentIdentityHeader';
import { AgentModelPanel } from '@/components/services/detail/agents/AgentModelPanel';
import { AgentCapabilitiesPanel } from '@/components/services/detail/agents/AgentCapabilitiesPanel';
import { AgentPromptPanel } from '@/components/services/detail/agents/AgentPromptPanel';
import { AgentIoContractPanel } from '@/components/services/detail/agents/AgentIoContractPanel';
import { AgentActivityPanel } from '@/components/services/detail/agents/AgentActivityPanel';
import { AgentUsedByPanel } from '@/components/agents/AgentUsedByPanel';
import { AgentPromptsList } from '@/components/agents/AgentPromptsList';
import { AgentPromptSelector } from '@/components/agents/AgentPromptSelector';
import { VersionHistoryBody } from '@/components/versioning/VersionHistoryPanel';
import { ResourceVersionControls } from '@/components/versioning/ResourceVersionControls';
import { useActiveAgentPrompt, useAgent, useAgentPrompts, useDeleteAgent, useUpdateAgent } from '@/hooks/useAgents';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useResourceVersionStatus } from '@/hooks/useResourceVersions';
import { useToast } from '@/contexts/ToastContext';
import { asApiRefusal, refusalReason } from '@/utils/apiRefusal';
import type { Agent, UpdateAgentInput } from '@/types';

const TAB_NAMES = ['overview', 'prompt', 'io', 'runs', 'history'] as const;
type TabName = (typeof TAB_NAMES)[number];

const TAB_LABELS: Record<TabName, string> = {
  overview: 'Overview',
  prompt: 'Prompt',
  io: 'Input & Output',
  runs: 'Runs',
  history: 'History',
};

/** The tabs with an edit mode; on the others everything saves as it happens. */
const EDITABLE_TABS = new Set<TabName>(['overview', 'io']);

const PROMPT_SECTIONS = ['active', 'assigned', 'history'] as const;
type PromptSection = (typeof PROMPT_SECTIONS)[number];

/**
 * The keys Save may send, each with how "changed" is decided (task 1109's rule,
 * carried over). `metadata` is no longer edited on this page, so it is never
 * sent and any value an API client stored is preserved.
 */
const DEEP_KEYS = ['input', 'output', 'tools'] as const;
const SCALAR_KEYS = ['name', 'description', 'isActive', 'modelConfigId', 'failoverModelConfigId'] as const;

/** Structural equality for the JSON values in DEEP_KEYS. */
const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === null || b === null || a === undefined || b === undefined) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }

  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const leftKeys = Object.keys(left);
  if (leftKeys.length !== Object.keys(right).length) return false;
  return leftKeys.every(
    (key) => Object.prototype.hasOwnProperty.call(right, key) && deepEqual(left[key], right[key])
  );
};

/** The PATCH body: only the writable keys the User changed. */
const changedFields = (loaded: Agent, edited: Partial<Agent>): Record<string, unknown> => {
  const body: Record<string, unknown> = {};
  for (const key of DEEP_KEYS) {
    if (key in edited && !deepEqual(edited[key], loaded[key])) body[key] = edited[key];
  }
  for (const key of SCALAR_KEYS) {
    if (key in edited && edited[key] !== loaded[key]) body[key] = edited[key];
  }
  return body;
};

/** The service's reason for a refused update, with the failing field. */
const updateFailureDetail = (error: unknown): string => {
  const reason = refusalReason(error);
  const first = asApiRefusal(error).validationErrors?.[0];
  if (!first?.field) return reason;
  const message = first.message?.trim();
  if (!message || message === reason) return `${first.field}: ${reason}`;
  return `${reason} (${first.field}: ${message})`;
};

const primaryButton =
  'inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50';
const secondaryButton =
  'inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700';

/** Dims a section the current edit does not touch, and makes it ignore clicks. */
const lockedClasses = 'pointer-events-none select-none opacity-50';

export default function AgentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: agent, isLoading, error } = useAgent(id);
  const updateAgent = useUpdateAgent();
  const deleteAgent = useDeleteAgent();
  const canDelete = useFlag('innovate.fe.agents.delete');

  // Adoption of Publishing & versions is the gate for both History views.
  const versioningFlag = useFlag('innovate.fe.resource-versioning');
  const { status } = useResourceVersionStatus('agent', agent?.id);
  const versioningAdopted = versioningFlag && status.versioningEnabled;

  const visibleTabs: readonly TabName[] = versioningAdopted
    ? TAB_NAMES
    : TAB_NAMES.filter((name) => name !== 'history');

  const rawTab = searchParams.get('tab');
  const activeTab: TabName = (visibleTabs as readonly string[]).includes(rawTab ?? '')
    ? (rawTab as TabName)
    : 'overview';
  const selectedIndex = visibleTabs.indexOf(activeTab);

  const rawSection = searchParams.get('section');
  const promptSection: PromptSection =
    rawSection === 'assigned' || (rawSection === 'history' && versioningAdopted)
      ? rawSection
      : 'active';

  const [isEditing, setIsEditing] = useState(false);
  const [edits, setEdits] = useState<Partial<Agent>>({});
  const [promptSelectorOpen, setPromptSelectorOpen] = useState(false);

  const setQuery = useCallback(
    (values: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(values)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const selectTab = (name: TabName) => setQuery({ tab: name, section: null });
  const selectPromptSection = (section: PromptSection) => setQuery({ tab: 'prompt', section });

  const handleFieldChange = (field: string, value: unknown) => {
    setEdits((prev) => ({ ...prev, [field]: value }));
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEdits({});
  };

  const handleSave = async () => {
    if (!agent) return;
    const data = changedFields(agent, edits);
    if (typeof data.name === 'string' && !data.name.trim()) {
      toast.error('Agent name is required', 'Give the agent a name before saving.');
      return;
    }
    // Nothing changed: leave Edit without a request, and without claiming an
    // update that did not happen.
    if (Object.keys(data).length === 0) {
      cancelEdit();
      return;
    }
    try {
      // A cleared description or Model config is sent as `null`, which the
      // input type omits.
      await updateAgent.mutateAsync({ id: agent.id, data: data as UpdateAgentInput });
      cancelEdit();
      toast.success('Agent updated');
    } catch (saveError) {
      toast.error('Failed to update agent', updateFailureDetail(saveError));
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">Loading agent…</div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-red-500 dark:text-red-400">Error loading agent. Please try again.</div>
      </div>
    );
  }

  const draft: Agent = isEditing ? { ...agent, ...edits } : agent;
  const editingOverview = isEditing && activeTab === 'overview';
  const editingIo = isEditing && activeTab === 'io';

  const actions = isEditing ? (
    <>
      <button
        type="button"
        onClick={handleSave}
        disabled={updateAgent.isPending}
        data-testid="agent-save"
        className={primaryButton}
      >
        <CheckIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
        {updateAgent.isPending ? 'Saving…' : 'Save changes'}
      </button>
      <button
        type="button"
        onClick={cancelEdit}
        disabled={updateAgent.isPending}
        data-testid="agent-cancel"
        className={secondaryButton}
      >
        <XMarkIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
        Cancel
      </button>
    </>
  ) : EDITABLE_TABS.has(activeTab) ? (
    <button
      type="button"
      onClick={() => {
        setEdits({});
        setIsEditing(true);
      }}
      data-testid="agent-edit"
      className={secondaryButton}
    >
      <PencilIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
      Edit
    </button>
  ) : null;

  return (
    <div data-testid="agent-detail-page">
      <Breadcrumbs items={[{ name: 'Agents', href: '/agents' }, { name: agent.name }]} />

      <AgentIdentityHeader
        agent={draft}
        showMetadata
        actions={actions}
        editing={isEditing}
        onFieldChange={handleFieldChange}
        onOpenHistory={versioningAdopted ? () => selectTab('history') : undefined}
      />

      <Tab.Group
        selectedIndex={selectedIndex}
        onChange={(index) => {
          // Locked while editing: the other tabs ignore clicks until Save or Cancel.
          if (!isEditing) selectTab(visibleTabs[index] ?? 'overview');
        }}
      >
        <Tab.List className="mt-6 flex space-x-1 overflow-x-auto border-b border-gray-200 dark:border-gray-700">
          {visibleTabs.map((name) => {
            const locked = isEditing && name !== activeTab;
            return (
              <Tab
                key={name}
                disabled={locked}
                data-testid={`agent-tab-${name}`}
                data-locked={locked ? 'true' : undefined}
                className={({ selected }) =>
                  `whitespace-nowrap px-4 py-2 text-sm font-medium focus:outline-none ${
                    selected
                      ? 'border-b-2 border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  } ${locked ? 'cursor-not-allowed opacity-40' : ''}`
                }
              >
                {TAB_LABELS[name]}
              </Tab>
            );
          })}
        </Tab.List>

        <Tab.Panels className="mt-6">
          {visibleTabs.map((name) => (
            <Tab.Panel key={name} data-testid={`agent-panel-${name}`} className="focus:outline-none">
              {name === 'overview' && (
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <AgentModelPanel
                      agent={draft}
                      nodeLlmConfigId={undefined}
                      editing={editingOverview}
                      onFieldChange={handleFieldChange}
                    />
                    <AgentCapabilitiesPanel
                      agent={draft}
                      editing={editingOverview}
                      onFieldChange={handleFieldChange}
                    />
                  </div>

                  <div
                    className={`grid grid-cols-1 gap-4 lg:grid-cols-2 ${editingOverview ? lockedClasses : ''}`}
                    data-testid="agent-overview-readonly-row"
                  >
                    <AgentPromptPanel agentId={agent.id} fill />
                    <AgentUsedByPanel agentId={agent.id} />
                  </div>

                  {canDelete && (
                    <section
                      data-testid="agent-danger-zone"
                      data-locked={isEditing ? 'true' : undefined}
                      className={`pt-4 ${isEditing ? lockedClasses : ''}`}
                    >
                      <h3 className="text-sm font-semibold text-red-600 dark:text-red-400">Danger zone</h3>
                      <div className="mt-2 rounded-lg border border-red-300 bg-white p-4 shadow-sm dark:border-red-800 dark:bg-gray-800">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            Deleting moves this agent to your account&rsquo;s Archive, where it can be
                            restored. A delete is refused while a running service uses it; Used by lists
                            them.
                          </p>
                          <DeleteResourceControl
                            flag="innovate.fe.agents.delete"
                            entity="agents"
                            entityLabel="agent"
                            name={agent.name}
                            id={agent.id}
                            variant="button"
                            triggerLabel="Delete agent"
                            testId="agent-delete"
                            body={
                              <p>
                                The prompts, MCP connections and knowledge bases attached to it stay in
                                your library.
                              </p>
                            }
                            onDelete={() => deleteAgent.mutateAsync(agent.id)}
                            onDeleted={() => navigate('/agents')}
                          />
                        </div>
                      </div>
                    </section>
                  )}
                </div>
              )}

              {name === 'prompt' && (
                <PromptTab
                  agentId={agent.id}
                  section={promptSection}
                  versioningAdopted={versioningAdopted}
                  onSelectSection={selectPromptSection}
                  onAssign={() => setPromptSelectorOpen(true)}
                />
              )}

              {name === 'io' && (
                <AgentIoContractPanel agent={draft} editing={editingIo} onFieldChange={handleFieldChange} />
              )}

              {name === 'runs' && <AgentActivityPanel agentId={agent.id} collaborationConfigId={null} />}

              {name === 'history' && (
                <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <VersionHistoryBody
                        entityType="agent"
                        entityId={agent.id}
                        resourceName={agent.name}
                        showHeading
                      />
                    </div>
                    <ResourceVersionControls
                      entityType="agent"
                      entityId={agent.id}
                      resourceName={agent.name}
                      actionOnly
                    />
                  </div>
                </section>
              )}
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>

      <AgentPromptSelector
        agentId={agent.id}
        isOpen={promptSelectorOpen}
        onClose={() => setPromptSelectorOpen(false)}
      />
    </div>
  );
}

interface PromptTabProps {
  agentId: string;
  section: PromptSection;
  versioningAdopted: boolean;
  onSelectSection: (section: PromptSection) => void;
  onAssign: () => void;
}

const SECTION_LABELS: Record<PromptSection, string> = {
  active: 'Active',
  assigned: 'Assigned',
  history: 'History',
};

/**
 * The Prompt tab: left-side sub tabs Active · Assigned · History (report open
 * question 3: Assigned is which prompt runs, History is how the active one
 * changed, so they stay apart and cross-link). Everything here saves as it
 * happens, so the tab has no Edit mode.
 */
function PromptTab({ agentId, section, versioningAdopted, onSelectSection, onAssign }: PromptTabProps) {
  const { data: assigned } = useAgentPrompts(agentId);
  const { data: activePrompt } = useActiveAgentPrompt(agentId);
  const sections = PROMPT_SECTIONS.filter((entry) => entry !== 'history' || versioningAdopted);

  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-start">
      <nav aria-label="Prompt sections" className="flex gap-1 md:w-44 md:shrink-0 md:flex-col">
        {sections.map((entry) => (
          <button
            key={entry}
            type="button"
            onClick={() => onSelectSection(entry)}
            aria-current={section === entry ? 'page' : undefined}
            data-testid={`agent-prompt-section-${entry}`}
            className={`flex items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium ${
              section === entry
                ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200'
                : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
          >
            {SECTION_LABELS[entry]}
            {entry === 'assigned' && assigned && (
              <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">{assigned.length}</span>
            )}
          </button>
        ))}
      </nav>

      <div className="min-w-0 flex-1">
        {section === 'active' && <AgentPromptPanel agentId={agentId} full />}

        {section === 'assigned' && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Prompts linked to this agent; one is active at a time. Making another active changes
                what the agent runs on its next call.
              </p>
              <button
                type="button"
                onClick={onAssign}
                data-testid="agent-prompt-assign"
                className={primaryButton}
              >
                + Assign
              </button>
            </div>
            <AgentPromptsList
              agentId={agentId}
              onOpenHistory={versioningAdopted ? () => onSelectSection('history') : undefined}
            />
          </div>
        )}

        {section === 'history' && (
          <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            {activePrompt ? (
              <VersionHistoryBody
                entityType="prompt"
                entityId={activePrompt.id}
                resourceName={activePrompt.name}
                showHeading
              />
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No active prompt is assigned, so there is no prompt history to show.
              </p>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
